import { processSermonTimestampLinks, createCitationList, renderCitationFooter, renderSourceRows } from './sermonTimestampLinks';
import { linkScripture } from './scripture';

/**
 * Safe, small markdown → HTML for assistant messages.
 *
 * Order matters: the raw text is HTML-escaped FIRST, so nothing a model (or a
 * saved chat) contains can inject markup; every tag below is one we wrote.
 * Then citations become footnote markers, then links, inline styles, blocks
 * and paragraphs, and the sources the answer cited are listed at its foot.
 */

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, (c) => ESCAPES[c]);
}

/** The church's front door. "The Net" in an answer links here, once. */
export const THE_NET_URL = 'https://www.thenet.org/';

// [text](https://…) as the model writes it. http(s) only: anything else stays
// literal text. Label and URL arrive already escaped, so neither can leave
// the attribute or the element.
const MARKDOWN_LINK = /\[([^[\]\n]+)\]\((https?:\/\/(?:[^\s()<>]|\([^\s()<>]*\))+)(?:\s+&quot;[^\n]*?&quot;)?\)/g;

// The church by name: not a sermon series title ("The Net - Grace"), not a
// handle ("@TheNet"), not the start of a longer word, and not a title that
// runs on past the name ("The Net Draws", "Christmas Eve at The Net 2024").
const THE_NET = /(^|[^\w@#])([Tt]he Net(?: Community Church)?)(?!\w|\s+[-–]\s|[ \t]+(?:[A-Z][a-z]|(?:19|20)\d\d\b))/g;

// Titles that end on the name, read from the words before it, for when the
// catalog is not at hand (Focused Study has only its own sermons; the archive
// before the catalog loads): a year ("Mother's Day 2024 at The Net"), or a
// capital mid-sentence ("God's Vision For The Net", "In Why The Net, …").
const TITLE_BEFORE = /(?:(?:19|20)\d\d[ \t]+at|[^\s.!?…:;(“"][ \t]+[A-Z][\w&#;'’]*)[ \t]+$/;

const NOT_IN = ['a', 'code', 'cite', 'sup'];

const DECODE = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

/**
 * A quote as the server compared it, to tell a verified one by: no citation
 * (its name, its number), no markup, case, accents, quotation marks or
 * punctuation. Two quotes are the same words when their keys are equal.
 */
function quoteKey(html) {
  return String(html || '')
    .replace(/<sup\b[\s\S]*?<\/sup>|<cite\b[\s\S]*?<\/cite>/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(?:amp|lt|gt|quot|#39);/g, (m) => DECODE[m])
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[’‘`´]/g, "'")
    .replace(/[^a-z0-9']+/g, ' ')
    .replace(/(^|\s)'+|'+(?=\s|$)/g, '$1')
    .trim();
}

/**
 * Applies fn to the text between tags, skipping anything inside the named
 * elements. Every tag in the string is one we wrote, so splitting on <…> is
 * exact.
 */
function mapText(html, skip, fn) {
  let depth = 0;
  return html.split(/(<[^>]+>)/).map((part, i) => {
    if (i % 2) {
      const tag = /^<(\/?)([a-z0-9]+)/i.exec(part);
      if (tag && skip.includes(tag[2].toLowerCase())) depth = Math.max(0, depth + (tag[1] ? -1 : 1));
      return part;
    }
    return depth ? part : fn(part);
  }).join('');
}

/**
 * Titles that contain the name ("The Net Holds", "Mother's Day at The Net
 * 2026"), as patterns over escaped text: spelled out in the prose, the name
 * is part of the title. Apostrophes match straight or curly.
 */
function titlePatterns(titles) {
  return [...new Set(titles.filter(Boolean))]
    .filter((t) => /\bthe net\b/i.test(t) && !/^the net(?: community church)?$/i.test(t.trim()))
    .map((t) => new RegExp(escapeHtml(t.trim())
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/\s+/g, '\\s+')
      .replace(/&#39;/g, "(?:&#39;|[’‘])"), 'gi'));
}

/**
 * "The Net" / "The Net Community Church" → thenet.org: the first mention in
 * the prose only, never inside a citation, a link, code, a footnote or a
 * title the prose spells out, and not at all when the model already linked it.
 * Titles are known from the catalog and this answer's sources where they are
 * at hand, and by their shape (THE_NET, TITLE_BEFORE) where they are not.
 */
function linkTheNet(html, titles = []) {
  if (!/[Tt]he Net\b/.test(html)) return html;
  if (/<a\b[^>]*>(?:(?!<\/a>)[\s\S])*?[Tt]he Net\b/.test(html) || /href="https?:\/\/(?:www\.)?thenet\.org\/?"/.test(html)) return html;
  const patterns = titlePatterns(titles);
  let linked = false;
  return mapText(html, NOT_IN, (text) => {
    if (linked) return text;
    const spans = patterns.flatMap((re) => [...text.matchAll(re)].map((m) => [m.index, m.index + m[0].length]));
    return text.replace(THE_NET, (m, pre, name, offset) => {
      const at = offset + pre.length;
      if (linked || spans.some(([from, to]) => from <= at && at < to)) return m;
      if (TITLE_BEFORE.test(text.slice(0, at))) return m;
      linked = true;
      return `${pre}<a href="${THE_NET_URL}" target="_blank" rel="noopener noreferrer">${name}</a>`;
    });
  });
}

/**
 * @param {string} content markdown from the model
 * @param {object} [opts]
 * @param {Array}  [opts.videos]         catalog for title → link resolution
 * @param {Array}  [opts.sources]        this answer's retrieved sermons, posts and chapters
 * @param {string} [opts.singleVideoUrl] single-sermon mode target
 * @param {boolean}[opts.showTitle]      name the sermon in citation labels
 * @param {boolean}[opts.footer]         list the cited sources at the foot (default true;
 *                                       false while an answer is still streaming in)
 * @param {string} [opts.footerStyle]    'rows': the app's sources list (BRAND_SPEC §11.1),
 *                                       one linked row per source; default: the compact list
 * @param {boolean}[opts.verbatim]       quotes are Rob's own, verified words (a Rob-approved
 *                                       canonical answer): blockquotes get the serif style.
 *                                       Never for a live answer (BRAND_SPEC §6.2)
 * @param {Array}  [opts.verifiedQuotes] quotes the server found word for word in the chapter
 *                                       they were given to (a book source's quotes): a
 *                                       blockquote that is exactly one of them is serif, the
 *                                       only live words that ever are
 * @param {object} [opts.citations]      a createCitationList() to fill, to read the sources back
 * @param {boolean}[opts.bookReader]     book citations open the Genius reader (the app only)
 * @param {boolean}[opts.scripture]      Bible references become quiet links to the passage
 *                                       (utils/scripture.js): finished answers in the app only
 * @param {object} [opts.ground]         what was in front of the model (the server's ground):
 *                                       a book citation is numbered only for a chapter it
 *                                       read (utils/sermonTimestampLinks.js). Without it, as always
 * @returns {string} HTML
 */
export function renderMarkdown(content, opts = {}) {
  if (!content || typeof content !== 'string') return '';
  let html = escapeHtml(content.replace(/\r\n/g, '\n'));
  const showTitle = opts.showTitle !== false;
  const citations = opts.citations || createCitationList();

  // Citations first — they produce our own tags, which nothing below touches
  // (the bold/italic patterns need literal asterisks; the link passes skip
  // them), and collect the numbered sources for the footer.
  html = processSermonTimestampLinks(html, opts.videos || [], {
    sources: opts.sources || [],
    singleVideoUrl: opts.singleVideoUrl || null,
    showTitle,
    citations,
    bookReader: Boolean(opts.bookReader),
    ...(opts.ground ? { ground: opts.ground } : {}),
  });

  // Inline code
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');

  // Links the model wrote
  html = mapText(html, NOT_IN, (text) => text.replace(MARKDOWN_LINK, (m, label, url) =>
    `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`));

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Blockquotes and rules. Serif only for verified words: a Rob-approved
  // answer's, or a quote the server found word for word in the book.
  const verified = new Set((Array.isArray(opts.verifiedQuotes) ? opts.verifiedQuotes : []).map(quoteKey).filter(Boolean));
  html = html.replace(/^&gt; ?(.*)$/gm, (m, inner) => (opts.verbatim || (verified.size && verified.has(quoteKey(inner)))
    ? `<blockquote class="cv-verbatim">${inner}</blockquote>`
    : `<blockquote>${inner}</blockquote>`));
  html = html.replace(/^(?:---|\*\*\*|___)\s*$/gm, '<hr>');

  // Bold, then italics (single asterisks not part of a bold pair)
  html = html.replace(/\*\*([^*\n](?:[^*\n]|\*(?!\*))*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|[^\w*])\*([^\s*](?:[^*\n]*[^\s*])?)\*(?=[^\w*]|$)/g, '$1<em>$2</em>');

  // Lists: runs of "- " / "* " / "• " or "1. " lines
  html = html.replace(/(?:^|\n)((?:[-*•] .*(?:\n|$))+)/g, (m, block) =>
    `\n<ul>${block.trim().split('\n').map((l) => `<li>${l.replace(/^[-*•] /, '')}</li>`).join('')}</ul>\n`);
  html = html.replace(/(?:^|\n)((?:\d+[.)] .*(?:\n|$))+)/g, (m, block) =>
    `\n<ol>${block.trim().split('\n').map((l) => `<li>${l.replace(/^\d+[.)] /, '')}</li>`).join('')}</ol>\n`);

  // Bare URLs (not inside a link, code or a citation). An escaped double
  // quote ends one, so "https://…" keeps its quotes outside the link.
  html = mapText(html, NOT_IN, (text) => text.replace(/(^|[^"'>=\w])(https?:\/\/(?:[^\s<&]|&amp;|&#39;)*[^\s<.,;:!?)&])/g, (m, pre, url) =>
    `${pre}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`));

  // Bible references, when asked (not inside a link, code, a citation or a
  // footnote; utils/scripture.js also passes over a citation left unresolved).
  if (opts.scripture) html = mapText(html, NOT_IN, linkScripture);

  html = linkTheNet(html, [...(opts.videos || []), ...(opts.sources || [])].map((v) => v && v.title));

  // Paragraphs: blank lines separate blocks; single newlines become <br>
  const blocks = html.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const body = blocks
    .map((b) => (/^<(h[1-3]|ul|ol|blockquote|hr)\b/.test(b) ? b.replace(/\n(?!<)/g, '<br>') : `<p>${b.replace(/\n/g, '<br>')}</p>`))
    .join('');
  if (opts.footer === false) return body;
  return body + (opts.footerStyle === 'rows' ? renderSourceRows(citations, { showTitle, bookReader: Boolean(opts.bookReader) }) : renderCitationFooter(citations, { showTitle }));
}

/**
 * renderMarkdown for a finished answer in the app: the body, its numbered
 * sources as rows (renderSourceRows, drawn under the answer's actions), and
 * what it cited, so the answer's label can say how many sermons it drew on.
 */
export function renderAnswer(content, opts = {}) {
  if (!content || typeof content !== 'string') return { html: '', sourcesHtml: '', cited: [] };
  const citations = createCitationList();
  const html = renderMarkdown(content, { ...opts, citations, footer: false });
  const sourcesHtml = renderSourceRows(citations, { showTitle: opts.showTitle !== false, bookReader: Boolean(opts.bookReader) });
  return { html, sourcesHtml, cited: citations.entries };
}
